// UTM DA CAMPANHA NO BOTÃO DE WHATSAPP (22/10/2026 — pedido do cs2504, vale para TODOS os sites).
//
// O visitante chega por um link de campanha (?utm_source=…), navega por outras páginas e só
// então clica no WhatsApp. Sem isto o lead chegava no CRM como "WhatsApp 1": a mensagem não
// carrega nada da campanha e as UTMs da URL de entrada já tinham se perdido na navegação.
//
// 1) A 1ª visita com UTM fica guardada no navegador (localStorage) por 30 dias — a origem é a
//    PRIMEIRA: campanha nova dentro da validade não troca a guardada.
// 2) No clique do WhatsApp, a mensagem pré-preenchida ganha um CÓDIGO INVISÍVEL com as 4 UTMs,
//    colado no fim da 1ª palavra. Sem campanha, o código vai com os campos vazios: ainda prova
//    "veio do botão do site" e o CRM grava a origem "site".
//
// CONTRATO (o SITE escreve, o worker do CRM lê — imobtotal-app src/common/wa-utm-site; nunca
// mudar um lado só):
//
//     texto  = "utm1" + US + source + US + medium + US + campaign + US + id   (US = U+001F), UTF-8
//     código = U+2060 + 6 dígitos base-3 por byte + U+2060
//       alfabeto: U+200B = 0 · U+200C = 1 · U+200D = 2 (mais significativo primeiro)
//     regex do worker: /⁠((?:[​‌‍]{6}){8,2000})⁠/
//
//   Mínimo 8 bytes = 48 dígitos: nunca se confunde com o código do SD-0024 (utils/whatsappImobiliaria.ts,
//   1 a 40 dígitos, mesmo delimitador) — os dois convivem na mesma mensagem.
//   Código de rastreio NUNCA visível e nunca no fim do texto (o WhatsApp corta o "espaço" invisível
//   do fim) — mesma regra do SD-0024 v2.
//
// Links de COMPARTILHAR (sem destinatário) e links sem texto voltam INTACTOS.

import { decodificaSuave, ehLinkDeWhatsapp, RE_OUTRO_PARAM, RE_TEM_DESTINATARIO } from './urlPaginaNoWhats';

// Kill-switch do lado do site (o CRM tem o seu: WA_UTM_SITE=0).
export const UTM_NO_WHATS_ATIVO = true;

export type UtmVisita = { utm_source: string; utm_medium: string; utm_campaign: string; utm_id: string };

const CHAVE_STORAGE = 'imt_utm_primeira_visita';
export const VALIDADE_UTM_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_CAMPO = 100; // o worker corta no mesmo tamanho

const CAMPOS: (keyof UtmVisita)[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id'];

const DELIMITADOR = '⁠'; // WORD JOINER
const ALFABETO = ['​', '‌', '‍']; // ZWSP=0 · ZWNJ=1 · ZWJ=2
const RE_CODIGO_UTM_TODOS = /⁠(?:[​‌‍]{6}){8,2000}⁠/g;
// 1ª palavra (letras/números de qualquer alfabeto); o código vai colado no fim dela.
const RE_PRIMEIRA_PALAVRA = new RegExp('[\\p{L}\\p{N}]+', 'u');

const limpaCampo = (valor: unknown): string =>
    Array.from(
        String(valor ?? '')
            .replace(/[\u0000-\u001f\u007f​-‍⁠﻿]/g, '')
            .replace(/\s+/g, ' ')
            .trim(),
    )
        .slice(0, MAX_CAMPO)
        .join('')
        .trim();

/** UTMs da querystring de `href`; `null` quando nenhuma das 4 veio preenchida. */
export function utmDaUrl(href: string): UtmVisita | null {
    let params: URLSearchParams;
    try {
        params = new URL(href).searchParams;
    } catch {
        return null;
    }
    const utm = {} as UtmVisita;
    for (const campo of CAMPOS) utm[campo] = limpaCampo(params.get(campo));
    return CAMPOS.some((campo) => utm[campo]) ? utm : null;
}

type StorageMinimo = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const storagePadrao = (): StorageMinimo | null => {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
        return null; // navegador com storage bloqueado
    }
};

/** UTM da 1ª visita ainda válida (30 dias); `null` sem nada guardado, vencido ou ilegível. */
export function utmGuardada(agora = Date.now(), storage: StorageMinimo | null = storagePadrao()): UtmVisita | null {
    if (!storage) return null;
    try {
        const salvo = JSON.parse(storage.getItem(CHAVE_STORAGE) || 'null');
        if (!salvo || typeof salvo.t !== 'number' || agora - salvo.t > VALIDADE_UTM_MS || agora < salvo.t) return null;
        const utm = {} as UtmVisita;
        for (const campo of CAMPOS) utm[campo] = limpaCampo(salvo[campo]);
        return CAMPOS.some((campo) => utm[campo]) ? utm : null;
    } catch {
        return null;
    }
}

/**
 * Guarda as UTMs de `href` se ainda não houver uma 1ª visita válida (primeira origem vence).
 * Devolve a UTM que vale agora (a guardada ou a recém-guardada). Nunca lança.
 */
export function guardaUtmDaPrimeiraVisita(
    href: string,
    agora = Date.now(),
    storage: StorageMinimo | null = storagePadrao(),
): UtmVisita | null {
    const jaGuardada = utmGuardada(agora, storage);
    if (jaGuardada) return jaGuardada;
    const daUrl = utmDaUrl(href);
    if (!daUrl || !storage) return daUrl;
    try {
        storage.setItem(CHAVE_STORAGE, JSON.stringify({ ...daUrl, t: agora }));
    } catch {
        /* storage cheio/bloqueado: vale só para esta página */
    }
    return daUrl;
}

/** Código invisível do contrato (com os campos vazios quando não há campanha). */
export function codigoInvisivelUtm(utm: UtmVisita | null): string {
    const texto = ['utm1', ...CAMPOS.map((campo) => limpaCampo(utm?.[campo]))].join('\u001f');
    let digitos = '';
    const bytes = new TextEncoder().encode(texto);
    for (let b = 0; b < bytes.length; b++) {
        let resto = bytes[b];
        let seis = '';
        for (let i = 0; i < 6; i++) {
            seis = ALFABETO[resto % 3] + seis;
            resto = Math.floor(resto / 3);
        }
        digitos += seis;
    }
    return `${DELIMITADOR}${digitos}${DELIMITADOR}`;
}

/** Tira os códigos de UTM de um texto (o código do SD-0024 fica). */
export const semCodigoUtm = (texto: string): string => String(texto ?? '').replace(RE_CODIGO_UTM_TODOS, '');

/**
 * Link de WhatsApp com o código de UTM colado no fim da 1ª palavra da mensagem.
 *
 * - link que não é WhatsApp, de COMPARTILHAR (sem destinatário) ou sem texto volta INTACTO;
 * - IDEMPOTENTE: recorta o código anterior antes de colar o novo (clique repetido, re-render);
 * - mesma manipulação de string do anexaUrlDaPaginaNoWhats (sem new URL(): há href malformado
 *   em produção) e reencode com encodeURIComponent (o "+" do URLSearchParams vira "+" literal).
 */
export function anexaUtmNoWhats(href: string, utm: UtmVisita | null): string {
    if (!ehLinkDeWhatsapp(href) || !RE_TEM_DESTINATARIO.test(href)) return href;
    const marca = /([?&])text=/i.exec(href);
    if (!marca) return href;

    const inicioDoValor = marca.index + marca[0].length;
    const cabeca = href.slice(0, inicioDoValor);
    let cauda = href.slice(inicioDoValor);
    let resto = '';
    const corte = cauda.search(RE_OUTRO_PARAM);
    if (corte >= 0) {
        resto = cauda.slice(corte);
        cauda = cauda.slice(0, corte);
    }

    const mensagem = semCodigoUtm(decodificaSuave(cauda));
    const palavra = RE_PRIMEIRA_PALAVRA.exec(mensagem);
    if (!palavra) return href;
    const fimDaPalavra = palavra.index + palavra[0].length;
    const nova = `${mensagem.slice(0, fimDaPalavra)}${codigoInvisivelUtm(utm)}${mensagem.slice(fimDaPalavra)}`;

    return `${cabeca}${encodeURIComponent(nova)}${resto}`;
}
